import { useCallback, useState } from "react";
import { DIAGNOSTICS_API } from "admin/types/constants/diagnostics";
import type {
  DiagnosticListItem,
  DiagnosticTriageStatus,
} from "admin/types/interfaces/diagnostics";
import { useSWRConfig } from "swr";
import { apiMutate } from "~/utils/apiClient";

/**
 * Mutation hook for a diagnostic's triage status.
 *
 * PATCHes `/diagnostics/{id}/triage-status` and revalidates every diagnostics
 * list query so the triage table reflects the change (including drop-out when a
 * status filter is active). `updatingIds` lets the caller disable a row's
 * control while its request is in flight.
 */
export function useTriageStatus() {
  const { mutate } = useSWRConfig();
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());

  const updateTriageStatus = useCallback(
    async (id: string, triageStatus: DiagnosticTriageStatus): Promise<void> => {
      setUpdatingIds((prev) => new Set(prev).add(id));
      try {
        await apiMutate<DiagnosticListItem>(`${DIAGNOSTICS_API}/${id}/triage-status`, "PATCH", {
          triage_status: triageStatus,
        });
        // Revalidate all diagnostics list queries (keys vary by filter/page).
        await mutate(
          (key) => typeof key === "string" && key.startsWith(`${DIAGNOSTICS_API}/`),
          undefined,
          { revalidate: true }
        );
      } finally {
        setUpdatingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [mutate]
  );

  return { updateTriageStatus, updatingIds };
}
